import React, { useRef, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import GradientText from '../components/GradientText';
import TextType from '../components/TextType';
import awarenessImg from '../assets/images/awareness.png';
import controlImg from '../assets/images/control.png';
import freedomImg from '../assets/images/freedom.png';
import convergeImg from '../assets/images/converge.png';
import filterImg from '../assets/images/noise-filter.png';
import LightRays from '../components/LightRays';
import ctaBgImg from '../assets/images/cta-bg.png';
import OptimizedImage from '../components/OptimizedImage';
import Navbar from '../components/Navbar';
import StarBorder from '../components/StarBorder';
import LiquidEther from '../components/LiquidEther';

const plans = [
  {
    id: 'observer',
    name: 'Observer',
    symbol: '🜏',
    tagline: 'See the chains. Stop sleeping.',
    monthlyPrice: 19,
    yearlyPrice: 14,
    features: [
      'Core doctrine: Chains, Loops, Signals',
      'Monthly Reality Deconstruction Brief',
      'Loop Awareness Framework',
      'Silent consumption access',
    ],
    forWho: 'People who suspect the world is fake, but can\'t yet prove it.',
    highlighted: false,
  },
  {
    id: 'strategist',
    name: 'Strategist',
    symbol: '🜏',
    tagline: 'Everything in Observer, plus control.',
    monthlyPrice: 39,
    yearlyPrice: 29,
    features: [
      'Cut Protocols (habits, reactions, dependencies)',
      'Seed Planting Framework',
      'Cause–Effect Mapping (self + situations)',
      'Mask Protocol (public self vs true intent)',
      'Monthly case dissections, cold analysis',
    ],
    forWho: 'People who are done reacting and want leverage.',
    highlighted: true,
    badge: 'Most Chosen',
  },
  {
    id: 'architect',
    name: 'Architect',
    symbol: '🜏',
    tagline: 'Everything in Strategist, plus system-level power.',
    monthlyPrice: 79,
    yearlyPrice: 59,
    features: [
      'System Design Frameworks',
      'Seed Web Architecture',
      'Long-horizon planning (5–20 year thinking)',
      'Advanced mental & ritual protocols',
      'Rare transmissions, not discussion, not support',
    ],
    forWho: 'People who want outcomes to feel inevitable.',
    highlighted: false,
  },
];

const PricingSection: React.FC = () => {
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('yearly');

  return (
    <section className="relative z-10 py-24 sm:py-32 px-4 sm:px-6 lg:px-8">

      <div className="relative z-10 max-w-6xl mx-auto">
        {/* Heading */}
        <div className="text-center mb-10 sm:mb-16 md:mb-24">
          <p className="text-xs tracking-[0.12em] font-bold font-mono uppercase leading-none mb-5" style={{ color: '#D9B33C' }}>The Architect Path</p>
          <h2
            className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white mb-6"
            style={{ fontFamily: 'Inter, sans-serif' }}
          >
            Choose Your Level
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-base sm:text-lg leading-relaxed">
            Every plan is a door. Only one opens where you actually are.
          </p>

          {/* Billing toggle */}
          <div className="mt-8 inline-flex items-center gap-1 rounded-full p-1" style={{ border: '1px solid rgba(255,255,255,0.3)' }}>
            <button
              onClick={() => setBilling('monthly')}
              className="px-5 py-2 rounded-full text-sm font-medium tracking-wide transition-all duration-300"
              style={billing === 'monthly'
                ? { backgroundColor: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }
                : { color: 'rgba(255,255,255,0.4)', border: '1px solid transparent' }}
            >
              Monthly
            </button>
            <button
              onClick={() => setBilling('yearly')}
              className="px-5 py-2 rounded-full text-sm font-medium tracking-wide transition-all duration-300 flex items-center gap-2"
              style={billing === 'yearly'
                ? { backgroundColor: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.25)' }
                : { color: 'rgba(255,255,255,0.4)', border: '1px solid transparent' }}
            >
              Annually
              <span className="text-xs px-2 py-0.5 rounded-full font-mono"
                style={{ backgroundColor: 'rgba(251,191,36,0.15)', color: '#FBB724' }}>
                Save ~25%
              </span>
            </button>
          </div>
        </div>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6 items-center">
          {plans.map((plan) => {
            const price = billing === 'monthly' ? plan.monthlyPrice : plan.yearlyPrice;
            const isCenter = plan.highlighted;

            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl flex flex-col transition-all duration-300 ${
                  isCenter
                    ? 'md:-translate-y-4 md:scale-105 z-10'
                    : 'hover:scale-[1.02]'
                }`}
                style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  border: isCenter
                    ? '1px solid rgba(255,255,255,1)'
                    : '1px solid rgba(255,255,255,0.09)',
                  boxShadow: isCenter
                    ? '0 4px 24px rgba(0,0,0,0.4)'
                    : '0 4px 24px rgba(0,0,0,0.4)',
                  padding: isCenter ? '2rem' : '1.75rem',
                }}
              >
                {/* Badge */}
                {plan.badge && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                    <span className="px-4 py-1 rounded-full text-xs font-semibold tracking-widest uppercase"
                      style={{ backgroundColor: 'rgba(255,255,255,1)', color: '#0C0D12', border: '1px solid rgba(255,255,255,1)', fontFamily: 'IBM Plex Mono, monospace' }}>
                      {plan.badge}
                    </span>
                  </div>
                )}

                <h3 className={`font-bold mb-2 ${isCenter ? 'text-3xl sm:text-4xl' : 'text-2xl sm:text-3xl'}`}
                  style={{ fontFamily: 'Cinzel, serif', color: '#FFFFFF' }}>
                  {plan.name}
                </h3>
                <p className="text-xs sm:text-sm text-gray-500 italic mb-6 leading-relaxed">{plan.tagline}</p>

                {/* Price */}
                <div className="mb-6">
                  <div className="flex items-end gap-1">
                    <span className={`font-bold leading-none ${isCenter ? 'text-5xl sm:text-6xl' : 'text-4xl sm:text-5xl'}`}
                      style={{ color: '#fff' }}>
                      ${price}
                    </span>
                    <div className="mb-1 ml-1">
                      <div className="text-xs font-mono text-gray-400">USD</div>
                      <div className="text-xs text-gray-500">
                        Billed {billing === 'monthly' ? 'monthly' : 'annually'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* CTA Button */}
                {isCenter ? (
                  <StarBorder
                    as="button"
                    className="w-full mb-8"
                    color="white"
                    speed="6s"
                    innerStyle={{ background: '#18191D' }}
                  >
                    Enter this Path
                  </StarBorder>
                ) : (
                  <button
                    className="w-full mb-8 py-4 px-6 rounded-[20px] text-[16px] text-center font-medium transition-all duration-300 text-white"
                    style={{ background: '#18191D', border: '1px solid rgba(255,255,255,0.08)' }}
                  >
                    Enter this Path
                  </button>
                )}

                {/* Divider */}
                <div className="w-full h-px mb-6" style={{ backgroundColor: 'rgba(255,255,255,0.07)' }} />

                {/* Features */}
                <ul className="space-y-3 flex-1 mb-6">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <svg className="w-4 h-4 mt-0.5 flex-shrink-0" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8l3.5 3.5L13 4.5" stroke={isCenter ? 'rgba(255,255,255,0.8)' : 'rgba(255,255,255,0.25)'} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                      <span className="text-xs sm:text-sm leading-relaxed" style={{ color: isCenter ? '#D1D5DB' : '#C4C9D4' }}>
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                {/* For who */}
                <div className="mt-auto pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.06)' }}>
                  <p className="text-xs leading-relaxed" style={{ color: isCenter ? 'rgba(255,255,255,0.5)' : 'rgba(200,207,218,0.6)' }}>
                    <span className="font-mono tracking-wider" style={{ color: isCenter ? 'rgba(255,255,255,0.3)' : 'rgba(180,187,198,0.5)' }}>FOR: </span>
                    {plan.forWho}
                  </p>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom note */}
        <div className="mt-12 text-center">
          <div className="inline-flex items-center space-x-4">
            <div className="w-16 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
            <p className="text-xs text-gray-300 font-mono tracking-widest uppercase">Cancel anytime. No questions.</p>
            <div className="w-16 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent" />
          </div>
        </div>
      </div>
    </section>
  );
};

const faqItems: { q: string; a: React.ReactNode }[] = [
  {
    q: "1. What is The Architect Circle?",
    a: (
      <>
        <p className="mb-3">The Architect Circle is a private development group focused on:</p>
        <ul className="space-y-1 mb-4 pl-4">
          {["Strategic thinking","Personal agency","Emotional discipline","Long-term cause-and-effect design","Building systems instead of reacting to them"].map(i => (
            <li key={i} className="flex items-start gap-2"><span className="text-amber-300/50 mt-1">·</span><span>{i}</span></li>
          ))}
        </ul>
        <p className="mb-1">We study how decisions compound over time, in career, relationships, reputation, and power.</p>
        <p className="mt-3 text-amber-100/80 italic">This is not mysticism. It is applied causality.</p>
      </>
    ),
  },
  {
    q: "2. Is this a religion or spiritual movement?",
    a: (
      <>
        <p className="mb-3">No. While we use symbolic language (chains, loops, seeds), these are metaphors for:</p>
        <ul className="space-y-1 mb-4 pl-4">
          {["Habits","Psychological patterns","Social dynamics","Behavioral feedback systems"].map(i => (
            <li key={i} className="flex items-start gap-2"><span className="text-amber-300/50 mt-1">·</span><span>{i}</span></li>
          ))}
        </ul>
        <p>There is no worship, no supernatural claims, and no required belief system. We treat reality as structured, and therefore engineerable.</p>
      </>
    ),
  },
  {
    q: "3. What do \"Chains\" and \"Loops\" actually mean?",
    a: (
      <>
        <p className="mb-4"><span className="text-amber-200 font-medium">Chains</span> = patterns that repeatedly produce the same outcomes.</p>
        <p className="mb-2">Examples:</p>
        <ul className="space-y-1 mb-5 pl-4">
          {["Financial instability cycles","Toxic relationship patterns","Self-sabotaging behaviors"].map(i => (
            <li key={i} className="flex items-start gap-2"><span className="text-amber-300/50 mt-1">·</span><span>{i}</span></li>
          ))}
        </ul>
        <p className="mb-4"><span className="text-amber-200 font-medium">Loops</span> = feedback systems that keep you reacting instead of designing.</p>
        <p>Our work is identifying them, then either cutting them or using them strategically.</p>
      </>
    ),
  },
  {
    q: "4. What does the Circle actually do?",
    a: (
      <>
        <p className="mb-4">Members operate on three levels:</p>
        <div className="space-y-4">
          {[
            { title: "Observer", desc: "Learns to identify personal patterns and reactive behavior." },
            { title: "Strategist", desc: "Begins designing deliberate cause,effect chains:", items: ["Career positioning","Social leverage","Reputation management","Influence without exposure"] },
            { title: "Architect", desc: "Builds systems that operate without constant effort:", items: ["Automated income streams","Strategic networks","Personal brand architecture","Behavioral frameworks"] },
          ].map(tier => (
            <div key={tier.title} className="pl-4">
              <p className="text-amber-200 font-medium mb-1 text-base sm:text-lg">{tier.title}</p>
              <p className="text-gray-300 text-base sm:text-lg mb-1">{tier.desc}</p>
              {tier.items && (
                <ul className="space-y-0.5 pl-2">
                  {tier.items.map(i => (
                    <li key={i} className="flex items-start gap-2 text-base sm:text-lg text-gray-400"><span className="text-amber-300/50 mt-1">·</span><span>{i}</span></li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
        <p className="mt-4 text-amber-100/70 italic">The goal is inevitability, outcomes that feel natural because the structure supports them.</p>
      </>
    ),
  },
  {
    q: "5. What is the \"Contact Protocol\"?",
    a: (
      <>
        <p className="mb-3">The Contact Protocol is not mystical. It is an advanced introspective exercise designed to:</p>
        <ul className="space-y-1 mb-4 pl-4">
          {["Strip emotional noise","Reduce reactive thinking","Increase clarity under pressure","Strengthen detached observation"].map(i => (
            <li key={i} className="flex items-start gap-2"><span className="text-amber-300/50 mt-1">·</span><span>{i}</span></li>
          ))}
        </ul>
        <p>It trains members to respond instead of react. We call it &ldquo;contact&rdquo; because it feels like accessing a clearer layer of perception, not because of supernatural experiences.</p>
      </>
    ),
  },
  {
    q: "6. What kind of person thrives here?",
    a: (
      <>
        <p className="mb-3">People who:</p>
        <ul className="space-y-1 mb-5 pl-4">
          {["Take responsibility instead of blaming","Can handle blunt feedback","Prefer strategy over drama","Value quiet power over attention","Think long-term"].map(i => (
            <li key={i} className="flex items-start gap-2"><span className="text-amber-300/50 mt-1">·</span><span>{i}</span></li>
          ))}
        </ul>
        <p className="text-amber-100/80 italic">This is not a support group. It is not therapy. It is not escapism. It is structured self-refinement.</p>
      </>
    ),
  },
  {
    q: "7. What is expected of members?",
    a: (
      <div className="space-y-4">
        {[
          { period: "Daily", items: ["Identify one reactive pattern","Replace it with deliberate action"] },
          { period: "Weekly", items: ["Map one cause,effect chain in your life","Improve it"] },
          { period: "Monthly", items: ["Audit long-term direction","Remove at least one self-limiting system"] },
        ].map(block => (
          <div key={block.period} className="pl-4">
            <p className="text-amber-200 font-medium mb-1 text-base sm:text-lg">{block.period}</p>
            <ul className="space-y-0.5">
              {block.items.map(i => (
                <li key={i} className="flex items-start gap-2 text-base sm:text-lg text-gray-300"><span className="text-amber-300/50 mt-1">·</span><span>{i}</span></li>
              ))}
            </ul>
          </div>
        ))}
        <p className="pt-1 text-amber-100/70 italic">Consistency matters more than intensity.</p>
      </div>
    ),
  },
  {
    q: "8. What is the ultimate goal?",
    a: (
      <>
        <p className="text-amber-200 font-medium text-lg mb-3">Self-sovereignty.</p>
        <p className="mb-3">To:</p>
        <ul className="space-y-1 mb-4 pl-4">
          {["Act deliberately","Build strategically","Remain emotionally controlled","Design life instead of drifting through it"].map(i => (
            <li key={i} className="flex items-start gap-2"><span className="text-amber-300/50 mt-1">·</span><span>{i}</span></li>
          ))}
        </ul>
        <p className="text-amber-100/80 italic">Not domination of others, mastery of self and structure.</p>
      </>
    ),
  },
];

const FAQAccordion: React.FC = () => {
  const [openIdx, setOpenIdx] = useState<number | null>(null);

  return (
    <div className="space-y-px">
      {faqItems.map((item, idx) => (
        <FAQItem
          key={idx}
          question={item.q}
          answer={item.a}
          open={openIdx === idx}
          onToggle={() => setOpenIdx(openIdx === idx ? null : idx)}
          isLast={idx === faqItems.length - 1}
        />
      ))}
    </div>
  );
};

const FAQItem: React.FC<{ question: string; answer: React.ReactNode; open: boolean; onToggle: () => void; isLast?: boolean }> = ({ question, answer, open, onToggle, isLast }) => {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className="transition-colors duration-300"
      style={{ borderBottom: isLast ? 'none' : `1px solid ${open ? 'rgba(251,191,36,0.15)' : 'rgba(255,255,255,0.06)'}` }}
    >
      <button
        className="w-full flex items-center justify-between py-5 sm:py-6 text-left gap-4 group"
        onClick={onToggle}
        aria-expanded={open}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <span
          className="text-base sm:text-lg font-semibold transition-colors duration-300"
          style={{ fontFamily: 'Inter, sans-serif', color: open || hovered ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.5)' }}
        >
          {question}
        </span>
        <span
          className="flex-shrink-0 transition-all duration-300"
          style={{
            color: open ? 'rgba(255,255,255,0.6)' : 'rgba(255,255,255,0.3)',
            transform: open ? 'rotate(45deg)' : 'rotate(0deg)',
          }}
        >
          <svg width="18" height="18" viewBox="0 0 10 10" fill="none">
            <line x1="5" y1="1" x2="5" y2="9" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
            <line x1="1" y1="5" x2="9" y2="5" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/>
          </svg>
        </span>
      </button>
      <div
        className="overflow-hidden transition-all duration-500 ease-in-out"
        style={{ maxHeight: open ? '800px' : '0px', opacity: open ? 1 : 0 }}
      >
        <div className="pb-6 text-base sm:text-lg text-gray-400 leading-relaxed space-y-1 pr-8 font-normal" style={{ fontFamily: 'Inter, sans-serif' }}>
          {answer}
        </div>
      </div>
    </div>
  );
};

const MOBILE_BREAKPOINT = 768;

const LandingPage: React.FC = () => {
  const mainContentRef = useRef<HTMLDivElement>(null);
  const ctaSectionRef = useRef<HTMLElement>(null);
  const topicsSectionRef = useRef<HTMLDivElement>(null);
  const heroSectionRef = useRef<HTMLElement>(null);
  const [fadeOpacity, setFadeOpacity] = useState(1);
  const [topicsOpacity, setTopicsOpacity] = useState(0);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.innerWidth < MOBILE_BREAKPOINT);
  const location = useLocation();

  useEffect(() => {
    if (location.hash === '#pricing') {
      setTimeout(() => {
        document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [location.hash]);

  useEffect(() => {
    const handleScroll = () => {
      if (!ctaSectionRef.current || !mainContentRef.current) return;

      const ctaRect = ctaSectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Start fading only when CTA is entering the bottom of viewport (much later)
      const fadeStart = windowHeight * 0.9; // Start when CTA top is at 90% of viewport height
      const fadeEnd = windowHeight * 0.5; // Complete fade when CTA top reaches viewport center
      
      // Calculate opacity based on scroll position
      if (ctaRect.top < fadeStart && ctaRect.top > fadeEnd) {
        // Fade out as CTA enters viewport
        const progress = (fadeStart - ctaRect.top) / (fadeStart - fadeEnd);
        const opacity = Math.max(0, 1 - progress);
        setFadeOpacity(opacity);
      } else if (ctaRect.top <= fadeEnd) {
        // Fully faded when CTA top reaches viewport center
        setFadeOpacity(0);
      } else {
        // Fully visible when CTA is far below viewport
        setFadeOpacity(1);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleTopicsFadeIn = () => {
      if (!topicsSectionRef.current) return;

      const topicsRect = topicsSectionRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      
      // Start fading in when topics section enters viewport
      // Begin fade when topics top reaches 80% of viewport height
      const fadeStart = windowHeight * 0.8;
      // Complete fade when topics top reaches 50% of viewport height
      const fadeEnd = windowHeight * 0.5;
      
      if (topicsRect.top < fadeStart && topicsRect.top > fadeEnd) {
        // Fade in as topics enter viewport
        const progress = (fadeStart - topicsRect.top) / (fadeStart - fadeEnd);
        const opacity = Math.min(1, Math.max(0, progress));
        setTopicsOpacity(opacity);
      } else if (topicsRect.top <= fadeEnd) {
        // Fully visible when topics top reaches viewport center
        setTopicsOpacity(1);
      } else {
        // Hidden when topics are below viewport
        setTopicsOpacity(0);
      }
    };

    window.addEventListener('scroll', handleTopicsFadeIn, { passive: true });
    handleTopicsFadeIn(); // Initial check

    return () => {
      window.removeEventListener('scroll', handleTopicsFadeIn);
    };
  }, []);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < MOBILE_BREAKPOINT);
    window.addEventListener('resize', check);
    check();
    return () => window.removeEventListener('resize', check);
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0C0D12' }}>
      {/* Navbar */}
      <Navbar />
      
      {/* Header/Hero Section */}
      <header ref={heroSectionRef} className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 sm:px-6 lg:px-8 pt-16 md:pt-20">
        {/* LightRays Background — responsive: fixed beam on mobile, follow cursor on desktop */}
        <div className="absolute inset-0 overflow-hidden" style={{ backgroundColor: '#0C0D12' }}>
          <LightRays
            raysOrigin="top-center"
            raysColor="#ffffff"
            raysSpeed={isMobile ? 0.6 : 1}
            lightSpread={isMobile ? 0.65 : 0.5}
            rayLength={isMobile ? 2.2 : 3}
            followMouse={!isMobile}
            mouseInfluence={isMobile ? 0 : 0.25}
            noiseAmount={0}
            distortion={0}
            pulsating={false}
            fadeDistance={isMobile ? 0.85 : 1}
            saturation={1}
          />
          {/* Dark Overlay for Better Contrast */}
          <div className="absolute inset-0 bg-black/5"></div>
          
          {/* Bottom Gradient Fade - Smooth transition to topics section */}
          <div 
            className="absolute bottom-0 left-0 right-0 h-48 sm:h-56 md:h-64 lg:h-80"
            style={{
              background: 'linear-gradient(to top, rgba(12, 13, 18, 1) 0%, rgba(12, 13, 18, 0.95) 20%, rgba(12, 13, 18, 0.7) 50%, rgba(12, 13, 18, 0.2) 80%, transparent 100%)',
              zIndex: 1,
              pointerEvents: 'none'
            }}
          ></div>
        </div>
        
        {/* Title Overlay */}
        <div className="absolute inset-0 flex items-center justify-center text-center z-10">
          <div className="flex flex-col items-center justify-center space-y-4 sm:space-y-6" style={{ marginTop: '100px' }}>
            <h1 className="text-4xl font-bold sm:text-5xl md:text-6xl lg:text-7xl text-white leading-tight drop-shadow-[0_0_20px_rgba(255,248,220,0.9),0_0_40px_rgba(255,250,240,0.6)]" style={{ letterSpacing: '-0.01em' }}>
              From Clarity to Choice to{' '}
              <GradientText
                className="font-charm"
                colors={['#fdc708', '#7ee2fb', '#6c47f0', '#fdc708']}
                animationSpeed={6}
              >
                Change
              </GradientText>
            </h1>
            <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-white/90 font-normal leading-relaxed drop-shadow-[0_0_10px_rgba(255,248,220,0.7),0_0_20px_rgba(255,250,240,0.4)]">
              <TextType
                as="span"
                text={[
                  "A clearer mind for a more intentional life.",
                  "Build habits that actually stick.",
                  "Design your life with purpose."
                ]}
                typingSpeed={55}
                deletingSpeed={25}
                pauseDuration={3000}
                initialDelay={800}
                loop
                showCursor
                cursorCharacter="|"
                cursorBlinkDuration={0.5}
              />
            </p>
            <a
              href="#pricing"
              onClick={(e) => { e.preventDefault(); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="px-5 sm:px-6 md:px-8 py-2 sm:py-2.5 md:py-3 text-base sm:text-lg md:text-xl font-sans font-bold transition-all duration-300 rounded-full text-gray-900 hover:bg-gray-200 hover:shadow-lg hover:scale-105 mt-8 sm:mt-10 md:mt-12"
              style={{ backgroundColor: '#F9F9F9', marginTop: '120px' }}
            >
              Join Inner Circle
            </a>
          </div>
        </div>
      </header>


      {/* Main Content Grid */}
      <main 
        ref={mainContentRef}
        style={{ 
          backgroundColor: '#0C0D12', 
          boxShadow: 'none',
          opacity: fadeOpacity,
          transition: 'opacity 0.6s ease-out'
        }} 
        className="shadow-none overflow-hidden relative"
      >
        
        <div className="w-full">
          <div 
            ref={topicsSectionRef}
            className="flex flex-col"
            style={{
              opacity: topicsOpacity,
              transition: 'opacity 0.6s ease-out',
            }}
          >
            
            {/* Section 1: Awareness - Image Left, Text Right */}
            <div className="relative overflow-hidden">
              <div className="flex flex-col lg:flex-row min-h-[50vh] lg:h-[50vh] relative">
                <div className="w-full lg:w-1/2 order-1 lg:order-1 flex relative">
                  <div className="w-full h-full relative">
                    <OptimizedImage
                      src={awarenessImg}
                      alt="Awareness"
                      className="w-full h-64 sm:h-80 md:h-96 lg:h-full"
                      style={{ filter: 'blur(0.5px)' }}
                      imgStyle={{ objectPosition: 'center 25%' }}
                      loading="eager"
                    />
                    {/* Noise Filter Overlay */}
                    <div 
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      style={{
                        backgroundImage: `url(${filterImg})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        mixBlendMode: 'overlay',
                        opacity: 0.6
                      }}
                    ></div>
                  </div>
                </div>
                <div className="w-full lg:w-1/2 order-2 lg:order-2 flex flex-col justify-center px-6 sm:px-6 md:px-8 lg:px-12 xl:px-20 py-6 lg:py-8 relative overflow-hidden">
                  {/* Blurred background image */}
                  <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      backgroundImage: `url(${awarenessImg})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      filter: 'blur(40px)',
                      WebkitFilter: 'blur(40px)',
                      transform: 'scale(1.2)'
                    }}
                  ></div>
                  {/* Noise Filter Overlay */}
                  <div 
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    style={{
                      backgroundImage: `url(${filterImg})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      mixBlendMode: 'overlay',
                      opacity: 0.4
                    }}
                  ></div>
                  {/* Dark overlay for text readability */}
                  <div className="absolute inset-0 bg-black/40 pointer-events-none"></div>
                  <div className="relative z-10">
                    <h2 className="jacquard-font mb-4 sm:mb-6 text-3xl sm:text-4xl lg:text-5xl text-white">Awareness</h2>
                    <div className="mb-6 sm:mb-8 lg:mb-10 space-y-4 sm:space-y-5">
                      <p className="text-gray-300 leading-relaxed text-base sm:text-lg lg:text-xl" style={{ letterSpacing: '-0.01em' }}>
                        We are architects of cause, trained to notice the forces most people never see. This isn't belief. It's controlled input.
                      </p>
                      <p className="text-gray-300 leading-relaxed text-base sm:text-lg lg:text-xl" style={{ letterSpacing: '-0.01em' }}>
                        With awareness and intentional action, we reshape outcomes and escape the loops that quietly hold the unaware in place.
                      </p>
                    </div>
                    <Link to="/doctrine" className="glow-button w-fit px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base mb-4 sm:mb-6 lg:mb-8 xl:mb-0 inline-block">Read Doctrine</Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Control - Text Left, Image Right */}
            <div className="relative overflow-hidden">
              <div className="flex flex-col lg:flex-row min-h-[50vh] lg:h-[50vh] relative">
                <div className="w-full lg:w-1/2 order-1 lg:order-2 flex relative">
                  <div className="w-full h-full relative">
                    <OptimizedImage
                      src={controlImg}
                      alt="Control"
                      className="w-full h-64 sm:h-80 md:h-96 lg:h-full"
                      style={{ filter: 'blur(0.5px)' }}
                      loading="eager"
                    />
                    {/* Noise Filter Overlay */}
                    <div 
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      style={{
                        backgroundImage: `url(${filterImg})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        mixBlendMode: 'overlay',
                        opacity: 0.6
                      }}
                    ></div>
                  </div>
                </div>
                <div className="w-full lg:w-1/2 order-2 lg:order-1 flex flex-col justify-center px-6 sm:px-6 md:px-8 lg:px-12 xl:px-20 py-6 lg:py-8 relative overflow-hidden">
                  {/* Blurred background image */}
                  <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      backgroundImage: `url(${controlImg})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      filter: 'blur(40px)',
                      WebkitFilter: 'blur(40px)',
                      transform: 'scale(1.2)'
                    }}
                  ></div>
                  {/* Noise Filter Overlay */}
                  <div 
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    style={{
                      backgroundImage: `url(${filterImg})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      mixBlendMode: 'overlay',
                      opacity: 0.4
                    }}
                  ></div>
                  {/* Dark overlay for text readability */}
                  <div className="absolute inset-0 bg-black/40 pointer-events-none"></div>
                  <div className="relative z-10">
                    <h2 className="jacquard-font mb-4 sm:mb-6 text-3xl sm:text-4xl lg:text-5xl text-white">Control</h2>
                    <div className="mb-6 sm:mb-8 lg:mb-10 space-y-4 sm:space-y-5">
                      <p className="text-gray-300 leading-relaxed text-base sm:text-lg lg:text-xl" style={{ letterSpacing: '-0.01em' }}>
                        We are not here to hope or pray. We are here to design.
                      </p>
                      <p className="text-gray-300 leading-relaxed text-base sm:text-lg lg:text-xl" style={{ letterSpacing: '-0.01em' }}>
                        Control is not domination, it is the quiet placement of causes that make outcomes inevitable. We study the chains beneath every choice, then rewrite them, turning reaction into intention and chaos into structure.
                      </p>
                    </div>
                    <Link to="/lore" className="glow-button w-fit px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base mb-4 sm:mb-6 lg:mb-8 xl:mb-0 inline-block">Read Lore</Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Freedom - Image Left, Text Right */}
            <div className="relative overflow-hidden">
              <div className="flex flex-col lg:flex-row min-h-[50vh] lg:h-[50vh] relative">
                <div className="w-full lg:w-1/2 order-1 lg:order-1 flex relative">
                  <div className="w-full h-full relative">
                    <OptimizedImage
                      src={freedomImg}
                      alt="Freedom"
                      className="w-full h-64 sm:h-80 md:h-96 lg:h-full"
                      style={{ filter: 'blur(0.5px)' }}
                      imgStyle={{ objectPosition: 'center 30%' }}
                    />
                    {/* Noise Filter Overlay */}
                    <div 
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      style={{
                        backgroundImage: `url(${filterImg})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        mixBlendMode: 'overlay',
                        opacity: 0.4
                      }}
                    ></div>
                  </div>
                </div>
                <div className="w-full lg:w-1/2 order-2 lg:order-2 flex flex-col justify-center px-6 sm:px-6 md:px-8 lg:px-12 xl:px-20 py-6 lg:py-8 relative overflow-hidden">
                  {/* Blurred background image */}
                  <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      backgroundImage: `url(${freedomImg})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      filter: 'blur(40px)',
                      WebkitFilter: 'blur(40px)',
                      transform: 'scale(1.2)'
                    }}
                  ></div>
                  {/* Noise Filter Overlay */}
                  <div 
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    style={{
                      backgroundImage: `url(${filterImg})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      mixBlendMode: 'overlay',
                      opacity: 0.4
                    }}
                  ></div>
                  {/* Dark overlay for text readability */}
                  <div className="absolute inset-0 bg-black/40 pointer-events-none"></div>
                  <div className="relative z-10">
                    <h2 className="jacquard-font mb-4 sm:mb-6 text-3xl sm:text-4xl lg:text-5xl text-white">Freedom</h2>
                    <div className="mb-6 sm:mb-8 lg:mb-10 space-y-4 sm:space-y-5">
                      <p className="text-gray-300 leading-relaxed text-base sm:text-lg lg:text-xl" style={{ letterSpacing: '-0.01em' }}>
                        True freedom isn't escape, it's mastery.
                      </p>
                      <p className="text-gray-300 leading-relaxed text-base sm:text-lg lg:text-xl" style={{ letterSpacing: '-0.01em' }}>
                        Breaking the loop is only the beginning; shaping the next one is the endgame. Freedom isn't drifting without structure, it's choosing which structures to build, and which to destroy.
                      </p>
                    </div>
                    <button className="glow-button w-fit px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base mb-4 sm:mb-6 lg:mb-8 xl:mb-0">Join Academy</button>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4: Converge - Text Left, Image Right */}
            <div className="relative shadow-none overflow-hidden" style={{ boxShadow: 'none' }}>
              <div className="flex flex-col lg:flex-row min-h-[50vh] lg:h-[50vh] relative">
                <div className="w-full lg:w-1/2 order-1 lg:order-2 flex relative">
                  <div className="w-full h-full relative">
                    <OptimizedImage
                      src={convergeImg}
                      alt="Converge"
                      className="w-full h-64 sm:h-80 md:h-96 lg:h-full"
                      style={{ filter: 'blur(0.5px)' }}
                      imgStyle={{ objectPosition: 'center 30%' }}
                      loading="eager"
                    />
                    {/* Noise Filter Overlay */}
                    <div 
                      className="absolute inset-0 w-full h-full pointer-events-none"
                      style={{
                        backgroundImage: `url(${filterImg})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        mixBlendMode: 'overlay',
                        opacity: 0.6
                      }}
                    ></div>
                  </div>
                </div>
                <div className="w-full lg:w-1/2 order-2 lg:order-1 flex flex-col justify-center px-6 sm:px-6 md:px-8 lg:px-12 xl:px-20 py-6 lg:py-8 relative overflow-hidden">
                  {/* Blurred background image */}
                  <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      backgroundImage: `url(${convergeImg})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      filter: 'blur(40px)',
                      WebkitFilter: 'blur(40px)',
                      transform: 'scale(1.2)'
                    }}
                  ></div>
                  {/* Noise Filter Overlay */}
                  <div 
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    style={{
                      backgroundImage: `url(${filterImg})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      backgroundRepeat: 'no-repeat',
                      mixBlendMode: 'overlay',
                      opacity: 0.4
                    }}
                  ></div>
                  {/* Dark overlay for text readability */}
                  <div className="absolute inset-0 bg-black/40 pointer-events-none"></div>
                  <div className="relative z-10">
                    <h2 className="jacquard-font mb-4 sm:mb-6 text-3xl sm:text-4xl lg:text-5xl text-white">Converge</h2>
                    <div className="mb-6 sm:mb-8 lg:mb-10 space-y-4 sm:space-y-5">
                      <p className="text-gray-300 leading-relaxed text-base sm:text-lg lg:text-xl" style={{ letterSpacing: '-0.01em' }}>
                        The chains are seen. The loops are broken. The seeds are planted. But no Architect works alone.
                      </p>
                      <p className="text-gray-300 leading-relaxed text-base sm:text-lg lg:text-xl" style={{ letterSpacing: '-0.01em' }}>
                        We meet where signals converge, outside the noise. Enter the circle. Step into the Discord. Build with us.
                      </p>
                    </div>
                    <button className="discord-button w-fit mb-4 sm:mb-6 lg:mb-8 xl:mb-0 inline-flex items-center space-x-2">
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.019 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1568 2.4189Z"/>
                      </svg>
                      <span>Join Discord</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Transition Element - Smooth fade between topics and CTA */}
      <div className="relative h-24 sm:h-32 md:h-40 lg:h-48 overflow-hidden" style={{ backgroundColor: '#0C0D12' }}>
        {/* Gradient fade overlay */}
        <div 
          className="absolute inset-0"
          style={{
            background: 'linear-gradient(to bottom, rgba(12, 13, 18, 0) 0%, rgba(12, 13, 18, 0.3) 30%, rgba(12, 13, 18, 0.7) 70%, rgba(12, 13, 18, 1) 100%)',
            zIndex: 1
          }}
        ></div>
      </div>

      {/* CTA Section */}
      <section 
        ref={ctaSectionRef}
        className="relative min-h-[65vh] sm:min-h-[75vh] flex items-center justify-center overflow-hidden px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16"
      >
        {/* Background Image */}
        <div 
          className="absolute inset-0"
          style={{
            backgroundImage: `url(${ctaBgImg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            filter: 'blur(4px)',
            WebkitFilter: 'blur(4px)'
          }}
        ></div>
        
        {/* Noise Filter Overlay */}
        <div 
          className="absolute inset-0 w-full h-full"
          style={{
            backgroundImage: `url(${filterImg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            mixBlendMode: 'overlay',
            opacity: 0.6,
            pointerEvents: 'none'
          }}
        ></div>
        
        {/* Top Gradient Fade - Smooth transition from previous section */}
        <div 
          className="absolute top-0 left-0 right-0 h-24 sm:h-32 md:h-40 lg:h-48"
          style={{
            background: 'linear-gradient(to bottom, rgba(12, 13, 18, 1) 0%, rgba(12, 13, 18, 0.8) 30%, rgba(12, 13, 18, 0.4) 70%, transparent 100%)',
            zIndex: 1,
            pointerEvents: 'none'
          }}
        ></div>
        
        {/* Bottom Gradient Fade - Smooth transition to footer */}
        <div 
          className="absolute bottom-0 left-0 right-0 h-24 sm:h-32 md:h-40 lg:h-48"
          style={{
            background: 'linear-gradient(to top, rgba(12, 13, 18, 1) 0%, rgba(12, 13, 18, 0.8) 30%, rgba(12, 13, 18, 0.4) 70%, transparent 100%)',
            zIndex: 1,
            pointerEvents: 'none'
          }}
        ></div>
        
        {/* Dark Overlay for Better Text Contrast */}
        <div className="absolute inset-0 bg-black/40"></div>
        
        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto text-center cta-container">
          <div className="space-y-4 sm:space-y-6 md:space-y-8">
            <div className="space-y-1 sm:space-y-2">
              <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-amber-100 leading-relaxed" style={{ letterSpacing: '-0.01em' }}>
                Most people react to outcomes.
              </p>
              <p className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl text-amber-100 leading-relaxed" style={{ letterSpacing: '-0.01em' }}>
                We design the causes behind them.
              </p>
            </div>
            <p className="cta-text text-xl font-normal sm:text-2xl md:text-3xl text-amber-50/90 leading-relaxed">
              Some doors open only from the inside.
            </p>
          </div>
          
          {/* Enhanced shining accent line */}
          <div className="mt-8 sm:mt-12 md:mt-16 relative">
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-amber-300/60 to-transparent mx-auto"></div>
          </div>
          
          {/* CTA Button */}
          <div className="mt-6 sm:mt-8 md:mt-12">
            <button
              className="cta-button px-4 sm:px-6 py-2 sm:py-3 text-sm sm:text-base tracking-wider font-mono"
              onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Join Inner Circle
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="relative py-20 sm:py-28 px-4 sm:px-6 lg:px-8" style={{ backgroundColor: '#0C0D12' }}>

        <div className="relative z-10 max-w-3xl mx-auto">
          {/* Heading */}
          <div className="text-center mb-14 sm:mb-20">
            <p className="text-xs tracking-[0.12em] font-bold font-mono uppercase leading-none mb-5" style={{ color: '#D9B33C' }}>FAQ</p>
            <h2
              className="text-4xl sm:text-5xl md:text-6xl font-bold text-white"
              style={{ fontFamily: 'Inter, sans-serif' }}
            >
              Frequently Asked Questions
            </h2>
          </div>

          {/* Accordion */}
          <FAQAccordion />
        </div>

      </section>

      {/* Pricing + Footer shared LiquidEther wrapper */}
      <div className="relative overflow-hidden" style={{ backgroundColor: '#0C0D12' }}>
        {/* Shared LiquidEther background — desktop only */}
        <div className="hidden lg:block absolute inset-0 pointer-events-none" style={{ opacity: 0.75 }}>
          <LiquidEther
            colors={['#89f3fb', '#f9d006', '#e6e6f4']}
            mouseForce={15}
            cursorSize={80}
            isViscous={false}
            viscous={30}
            iterationsViscous={4}
            iterationsPoisson={4}
            resolution={0.12}
            isBounce={false}
            autoDemo
            autoSpeed={0.3}
            autoIntensity={2.0}
            takeoverDuration={0.25}
            autoResumeDelay={2000}
            autoRampDuration={1.0}
            style={{ width: '100%', height: '100%' }}
          />
        </div>

        {/* Pricing Section */}
        <div id="pricing">
          <PricingSection />
        </div>

        {/* Footer */}
        <footer className="relative z-10 py-12 sm:py-16">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 sm:gap-12 mb-8 sm:mb-12 px-4 sm:px-6 lg:px-8">
            
            {/* Academy Info */}
            <div className="text-center lg:text-left lg:col-span-2">
              <h3 className="jacquard-font text-3xl sm:text-4xl mb-4">Order of Architects</h3>
              <p className="text-gray-400 text-sm sm:text-base leading-relaxed mb-4 max-w-md lg:max-w-lg mx-auto lg:mx-0">
                We are Architects of causality, seeing the invisible chains of reality and placing new ones by choice.
              </p>
              <div className="flex justify-center lg:justify-start space-x-4">
              </div>
            </div>

            {/* Quick Links */}
            <div className="text-center lg:text-left">
              <h4 className="text-white font-semibold mb-4 text-lg">Resources</h4>
              <ul className="space-y-2 text-sm sm:text-base">
                <li><a href="/doctrine" className="text-gray-400 hover:text-amber-300 transition-colors duration-300">Doctrine</a></li>
                <li><a href="/lore" className="text-gray-400 hover:text-amber-300 transition-colors duration-300">Lore</a></li>
                <li><a href="#" className="text-gray-400 hover:text-amber-300 transition-colors duration-300">Ritual</a></li>
                <li><a href="#" className="text-gray-400 hover:text-amber-300 transition-colors duration-300">Archives</a></li>
              </ul>
            </div>

            {/* Discord CTA */}
            <div className="text-center lg:text-left">
              <h4 className="text-white font-semibold mb-4 text-lg">Join the Community</h4>
              <p className="text-gray-400 text-sm sm:text-base mb-4">
                Connect with fellow Architects in our Discord server.
              </p>
              <a 
                href="#" 
                className="discord-button inline-flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.317 4.3698a19.7913 19.7913 0 00-4.8851-1.5152.0741.0741 0 00-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 00-.0785-.037 19.7363 19.7363 0 00-4.8852 1.515.0699.0699 0 00-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 00.0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 00.0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 00-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 01-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 01.0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 01.0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 01-.0066.1276 12.2986 12.2986 0 01-1.873.8914.0766.0766 0 00-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 00.0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 00.0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 00-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419-.019 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1568 2.4189Z"/>
                </svg>
                <span>Join Discord</span>
              </a>
            </div>
          </div>

          {/* Bottom Section */}
          <div className="pt-8 sm:pt-12 px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
              {/* Copyright */}
              <div className="text-center sm:text-left">
                <p className="text-gray-300 text-sm">
                  © 2026 Order of Architects. All rights reserved.
                </p>
              </div>

              {/* Legal Links */}
              <div className="flex flex-wrap items-center justify-center sm:justify-end gap-4 sm:gap-6 text-sm">
                <a href="#" className="text-gray-300 hover:text-white transition-colors duration-300">Privacy Policy</a>
                <a href="#" className="text-gray-300 hover:text-white transition-colors duration-300">Terms of Service</a>
              </div>
            </div>

            {/* Mystical Accent - Centered */}
            {/* <div className="mt-6 sm:mt-8 text-center">
              <div className="inline-flex items-center space-x-3 sm:space-x-4">
                <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-transparent via-amber-300/60 to-transparent"></div>
                <span className="text-xs sm:text-sm font-normal tracking-widest text-gray-500">FORGED IN CAUSALITY</span>
                <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-transparent via-amber-300/60 to-transparent"></div>
              </div>
            </div> */}
          </div>
        </footer>
      </div>
    </div>
  );
};

export default LandingPage;
